/**
 * The campus HQ building is generated from this file (components/campus/hq/hqModel.ts). Nothing else needs editing when you add, move or rename a link.
 *
 * adminNav.ts — admin sidebar information architecture.
 * Pinned links always show; labeled groups are collapsible (see AdminLayout).
 * `icon` is a RemixIcon name without the `ri-` prefix (the brand icon set,
 * loaded via src/colaberry/tokens/fonts.css).
 */
// `section` is the management-portal RBAC section key (mirrors backend
// mgmtRoles.ts SECTION_KEYS). AdminLayout hides a link/group when the logged-in
// admin's role can't access its section; the backend enforces the same.
// `newTab` renders the link as a plain <a target="_blank"> instead of a router
// <Link> (mirrors the portal sidebar's identical NavItem.newTab pattern) — for
// external destinations (My Day) or internal bridge-landing pages (AI Training)
// where the current admin tab should stay put. `requiresMgmtBridge` hides the
// link unless AuthContext's `hasPortalAccount` is true — true for either a
// bridge-minted staff session OR a direct admin_users login whose email is
// linked to a staff CommunityMember (see mgmtBridgeService.loadStaffPortalLinkByEmail).
// A legacy admin with no staff link at all has no enrollment to send to, so the
// link would always 403 for them.
export interface NavLink { path: string; label: string; icon: string; section?: string; newTab?: boolean; requiresMgmtBridge?: boolean; }
/**
 * C92: a LIVE sub-list under a group (its entries come from the server, row-scoped for the viewer), e.g. Interns -> Gov Contracts.
 * Declared here, not in the sidebar component, so the admin sidebar AND the Campus HQ building both render it from this one
 * place: add a sub-list here and both show it. `source` names the feed (adminSubNav.ts maps each feed to entries).
 */
export type NavSubListSource = 'gov' | 'agents';
export interface NavSubList {
  source: NavSubListSource; label: string; section: string; icon: string;
  /** The admin pages its entries open, as strict one-segment patterns: the Management Portal bridge accepts these (mgmtNext.ts). */
  paths: RegExp[];
}
export interface NavGroup { label: string | null; section: string; links: NavLink[]; subLists?: NavSubList[]; }

/** Always-visible quick set above the collapsible groups. */
export const PINNED_LINKS: NavLink[] = [
  { path: '/admin/dashboard', label: 'Dashboard', icon: 'dashboard-line', section: 'dashboard' },
  // Admin OS executive home. On 'dashboard' and NOT 'war_room': every scoped
  // role holds the former and none holds the latter, so building it on the War
  // Room's section would have stripped the landing page from five of eight
  // roles. Pinned rather than grouped because it is the destination the other
  // domains hang off.
  { path: '/admin/war-room', label: 'War Room', icon: 'radar-line', section: 'war_room' },
  { path: '/admin/trust', label: 'Trust Center', icon: 'shield-check-line', section: 'trust' },
  // Support role's sole surface (also visible to owner/admin who hold 'students').
  { path: '/admin/students', label: 'Student Story', icon: 'file-user-line', section: 'students' },
  // Portfolio review. Its own section so a Mentor can be granted THIS and nothing
  // else; mgmtRoles gives them ['dashboard','career_review'].
  { path: '/admin/career-review', label: 'Portfolio Review', icon: 'award-line', section: 'career_review' },
  // advisor.colaberry.ai's own "My Day" queue — a separate app with its own
  // Google SSO, so this is a plain external link, no session bridge needed.
  { path: 'https://advisor.colaberry.ai/my-day/', label: 'My Day', icon: 'calendar-check-line', section: 'students', newTab: true },
  // Reverse of "Management Portal": a staff member jumps back into their OWN
  // connected student portal account with no separate login. Lands on
  // /admin/ai-training-enter, which mints a portal token and redirects.
  { path: '/admin/ai-training-enter', label: 'AI Training', icon: 'book-open-line', section: 'students', newTab: true, requiresMgmtBridge: true },
];

export const NAV_GROUPS: NavGroup[] = [
  // Leads and Pipeline carry their own 'leads' section: it is the narrow slice
  // of this group a sales rep may reach (backend requireSalesOrAdmin covers
  // exactly these two surfaces). Everyone who holds 'revenue' also holds
  // 'leads', so this changes nothing for owner/admin/revenue identities.
  { label: 'Revenue', section: 'revenue', links: [
    { path: '/admin/revenue', label: 'Revenue', icon: 'money-dollar-circle-line' },
    // C8.2. Listed here rather than in UNLISTED_PATH_SECTIONS deliberately: a nav entry
    // maps the frontend half of the section contract through the group's own 'revenue'
    // key, so the two gates cannot drift the way an unlisted surface can.
    { path: '/admin/commercialization', label: 'Commercialization', icon: 'line-chart-line' },
    // R1. Listed here for the same reason as the line above: a nav entry maps the
    // frontend half of the section contract through this group's own 'revenue' key, so
    // the backend gate and the sidebar cannot drift the way an unlisted surface can.
    // The API sits under /api/admin/commercialization, which is already classified.
    { path: '/admin/revenue-room', label: 'Revenue Room', icon: 'bank-line' },
    { path: '/admin/refunds', label: 'Refunds', icon: 'refund-2-line' },
    { path: '/admin/leads', label: 'Leads', icon: 'group-line', section: 'leads' },
    { path: '/admin/pipeline', label: 'Pipeline', icon: 'filter-3-line', section: 'leads' },
    { path: '/admin/business-accounts', label: 'Business Accounts', icon: 'building-line' },
    { path: '/admin/opportunities', label: 'Opportunities', icon: 'line-chart-line' },
    { path: '/admin/funnel', label: 'Funnel', icon: 'filter-2-line' },
  ]},
  // Marketing — the social publishing workflow, in the order the work happens.
  //
  // Every one of these pages was built and routed, and until 2026-09-17 not one of them
  // appeared here: the sidebar advertised a single "Marketing" link to the analytics
  // dashboard, and the Composer, Content queue, Calendar, Publishing queue and Brands were
  // reachable only by typing a URL. The pages were shipped against a spec that listed
  // fifteen destinations, the nav was written for the ones that did not exist yet, and a
  // test (adminNavMarketing.test.ts) forbade the built ones from the sidebar - so each page
  // became an orphan on the day it landed and a green suite kept it there.
  //
  // `section: 'campaigns'` is deliberate and must not become 'marketing': it is the section
  // the BACKEND gate already maps these APIs to (mgmtSectionGate PATH_SECTION covers
  // /api/admin/brands and /api/admin/content), and a link whose section the API does not
  // recognise renders for someone the API will then 403.
  { label: 'Marketing', section: 'campaigns', links: [
    // LABELS AND ORDER COME FROM ALI'S PROTOTYPE (2026-10-07, artifact "Colaberry Marketing
    // Prototype"). Every path is unchanged, so no link, bookmark or test that names a route
    // breaks - this renames what the operator reads and reorders it to match the work:
    // write something, see everything, see when, build where it points, watch it go out,
    // read the result, and set the thing up.
    //
    // "Composer" and "Content" were the system's words for itself. "New post" and "All posts"
    // are what somebody is actually trying to do, which is the whole point of the redesign.
    // ONE ENTRY, on purpose. Ali, 2026-10-08: "Delete the link on the left side navigation so we
    // can just have one button for Marketing." Eight sidebar rows for one section made the rail
    // the navigation and the Overview a page nobody needed; now the Overview IS the navigation.
    //
    // THE PAGES ARE NOT DELETED - explicitly: "Don't delete the page." Every route still exists
    // and is reachable from the Overview, from a breadcrumb and from a bookmark. Deleting them
    // would delete the composer, which is where a post is written. See MarketingHub for the
    // entry points this replaced.
    { path: '/admin/marketing', label: 'Marketing', icon: 'broadcast-line' },
  ]},
  // Brands — its own group as of 2026-10-16, because brand admin became its own section key.
  //
  // This is a SECOND sidebar row next to Marketing, which narrows the 8 Oct instruction ("just
  // have one button for Marketing"). That instruction collapsed eight rows belonging to ONE
  // owner into one; this row belongs to a DIFFERENT owner. The product-experience intern holds
  // `brands` and nothing else, and without an entry here she signs in to a dashboard with no way
  // to reach the only page she has been granted — nav is gated by section, not by role, so there
  // is no way to show her a link inside a group she cannot see.
  //
  // Marketing keeps brands too (the role lists it), so Sarbjit sees this row as well. That is the
  // cost of the split and it is one row.
  { label: 'Brands', section: 'brands', links: [
    { path: '/admin/marketing/brands', label: 'Brands', icon: 'global-line' },
  ]},
  { label: 'Campaigns', section: 'campaigns', links: [
    { path: '/admin/campaigns', label: 'Campaigns', icon: 'megaphone-line' },
    { path: '/admin/communications', label: 'Communications', icon: 'chat-3-line' },
    { path: '/admin/visitors', label: 'Visitors', icon: 'eye-line' },
    // Explorer Growth OS Command Center (spec §26). Deliberately in the
    // Campaigns group: `section: 'campaigns'` is what the spec assigns the page,
    // and it is the same section the BACKEND gate already classifies
    // `/api/admin/explorer-growth` under (`mgmtSectionGate.ts`'s PATH_SECTION),
    // so nav visibility and API access agree by construction rather than by
    // luck. A link sitting in a group whose section the API does not recognise
    // is a link that renders for someone the API will then 403.
    { path: '/admin/explorer-growth', label: 'Explorer Growth', icon: 'radar-line' },
    // Growth Journey OS workspace (Phase 6, T613). Same group, same section, and for
    // the same reason the row above gives: `campaigns` is what the BACKEND gate
    // classifies `/api/admin/growth-journey` under (`mgmtSectionGate.ts`), so nav
    // visibility and API access agree by construction. The detail page at
    // `/admin/growth-journey/handoffs/:id` needs NO UNLISTED_PATH_SECTIONS row:
    // `sectionForPath` matches by longest prefix, so this entry already resolves it.
    // `adminNavGrowthJourney.test.ts` asserts that rather than leaving it to be assumed.
    { path: '/admin/growth-journey', label: 'Growth Journey', icon: 'route-line' },
  ]},
  { label: 'Lead Ingestion', section: 'lead_ingestion', links: [
    { path: '/admin/sources', label: 'Sources', icon: 'upload-cloud-2-line' },
    { path: '/admin/ingest-logs', label: 'Ingest Logs', icon: 'file-list-3-line' },
    { path: '/admin/routing-rules', label: 'Routing Rules', icon: 'node-tree' },
    { path: '/admin/autonomous', label: 'Autonomous', icon: 'lightbulb-flash-line' },
  ]},
  // AI Internship applications. Its own group because it has its own section
  // key: the queue carries an applicant's resume, phone number and interview
  // transcript, which is a wider grant than the Program group's curriculum work.
  // The section matches what mgmtSectionGate classifies /api/admin/internship
  // under, so nav visibility and API access agree by construction — a link that
  // renders for someone the API will then 403 is the failure this file keeps
  // warning about. Admissions (Dhee) holds this section, so she sees it here.
  //
  // The "Interns" group. Its link opens the Interns hub (the application queue + the Agents tab,
  // which stays gated INSIDE the page by `agents` so Admissions — who holds only `internship` —
  // never sees it). Gov Contracts is NO LONGER a page tab: it is a nested, expandable sub-node
  // rendered dynamically under this group by AdminLayout (one per-contract sub-item, gated
  // `gov_contracts`, so Admissions never sees it), each opening the contract's read-only cockpit at
  // /admin/internship/gov-contract/:deliveryProjectId — the same view the assigned intern sees.
  { label: 'Interns', section: 'internship', links: [
    { path: '/admin/internship', label: 'Interns', icon: 'user-follow-line' },
  ], subLists: [
    // C92: each pursuit with a build and an active intern; each agent domain (the sidebar and the building read these)
    { source: 'gov', label: 'Gov Contracts', section: 'gov_contracts', icon: 'government-line',
      paths: [/^\/admin\/internship\/gov-contract\/[A-Za-z0-9-]{1,64}$/] },
    { source: 'agents', label: 'Agents', section: 'agents', icon: 'robot-2-line',
      paths: [/^\/admin\/agents\/[A-Za-z0-9_-]{1,64}$/, /^\/admin\/internship\/agent\/[A-Za-z0-9_-]{1,64}$/] },
  ]},
  { label: 'Inbox & Content', section: 'inbox_content', links: [
    { path: '/admin/inbox', label: 'Inbox COS', icon: 'inbox-2-line' },
    { path: '/admin/missed-opportunities', label: 'Missed Opportunities', icon: 'mail-close-line' },
    { path: '/admin/content-queue', label: 'Content Queue', icon: 'article-line' },
  ]},
  // The Accelerator is the front door of the Program domain: cohorts, the
  // people in them, what they are taught, and the work they produce all hang
  // off /admin/accelerator's own tabs now. Five surfaces that used to sit here
  // as siblings were folded into those tabs (2026-09-08) and deliberately kept
  // OUT of this list so the sidebar stops competing with the page:
  //
  //   Community Roles          -> a section under the Cohorts tab
  //   Cert Prep / Case Studies / Projects -> Accelerator program-level tabs
  //   Feed Control Governance  -> already reachable from Curriculum > Feed Control
  //
  // Their ROUTES stay live and their pages are unchanged. What changes is only
  // that they are no longer sidebar entries — which is exactly why each one is
  // restated in UNLISTED_PATH_SECTIONS below. Dropping a nav entry without that
  // restatement makes sectionForPath() return null, and ProtectedRoute then
  // bounces every scoped identity off a page the API would have served. That
  // failure is the one the case-studies comment used to warn about here.
  { label: 'Program', section: 'program', links: [
    { path: '/admin/accelerator', label: 'Accelerator', icon: 'graduation-cap-line' },
    // Renamed from "Orchestration": this is the curriculum authoring surface
    // (Composer, Experience Studio, Timeline, Feed Control), and "Curriculum"
    // is what it is called everywhere except this label. The PATH is unchanged
    // so every existing deep link, bookmark and ?tab= link keeps working.
    { path: '/admin/orchestration', label: 'Curriculum', icon: 'flow-chart' },
    { path: '/admin/workforce', label: 'AI Organization', icon: 'team-line' },
    // AF2.1. Sits in the Program group and is therefore gated on 'program', the same section
    // the backend's agent-domain routes require. The page itself scopes by OWNERSHIP, so a
    // full admin who opens it sees only the domains they are accountable for, not all ten.
    // CARRIES ITS OWN SECTION, and that is the whole point of this line.
    // The route behind it, /api/admin/agent-domains-mine, is gated on 'dashboard'
    // because AF8.3 moved it there after all six interns got 403 on 'program'. The nav
    // filter uses `l.section ?? g.section`, so without this the link inherits the
    // Program group's 'program' and the people it was built for cannot see it. Found
    // live on 19 Oct when an intern's sidebar showed only Dashboard and Brands.
    // Same shape as Leads/Pipeline, which sit in the Revenue group on 'leads'.
    { path: '/admin/my-domain', label: 'My Domain', icon: 'robot-2-line', section: 'dashboard' },
    // AF4.2 — an intern asks for a platform change; a named human decides it.
    // CARRIES ITS OWN SECTION, for exactly the reason the My Domain line above records: the
    // people this was built for hold 'dashboard', and inheriting the Program group's 'program'
    // would 403 all seven of them on the surface meant for them. That failure has already
    // happened once here, on 19 Oct.
    //
    // 'dashboard' is the one section every mgmt role carries, so this link is also visible to a
    // mentor, a support rep and a community organizer. That is intended and safe: the DECIDE and
    // queue-read routes behind it carry `requireAdmin`, and the page renders the decision queue
    // only when the API returns it. Visibility here is not the access control.
    { path: '/admin/change-requests', label: 'Change Requests', icon: 'git-pull-request-line', section: 'dashboard' },
    // The programme-wide view of the same ten. Sits beside My Domain because the pair is
    // the distinction worth seeing in the nav: one is what you own, one is everything.
    { path: '/admin/all-domains', label: 'All Domains', icon: 'stack-line' },
    // Reese manager-growth mission, Phase 3 (T09/T10) — fleet-wide tool/
    // capability catalog, reconciled read-only across every real authority
    // source. Sibling to "AI Organization" (agents), not nested under it.
    { path: '/admin/tools', label: 'Tools', icon: 'tools-line' },
    // AI Project Factory Command Center (Phase 3). A delivery contract is program-domain work; the
    // backend mgmtSectionGate maps /api/admin/factory to 'program', so the nav and the API agree.
    { path: '/admin/factory', label: 'Project Factory', icon: 'node-tree' },
    // Gov-contract entry page (Phase 5 slice 1): best-fit proposals -> start -> the Command Center.
    // Same 'program' section as /admin/factory (backend mgmtSectionGate covers /api/admin/factory/*).
    { path: '/admin/gov-opportunities', label: 'Gov Opportunities', icon: 'government-line' },
    // Gov qualification workspace (Phase 2): review an opportunity's source evidence before a bid pursuit is
    // approved. Same 'program' section (backend mgmtSectionGate covers /api/admin/factory/qualification/*).
    { path: '/admin/gov-qualification', label: 'Gov Qualification', icon: 'shield-check-line' },
    // Our Services: Colaberry's own offering catalog, matched (advisory) against opportunities. Same 'program'
    // section (backend mgmtSectionGate covers /api/admin/factory/services).
    { path: '/admin/gov-services', label: 'Our Services', icon: 'service-line' },
  ]},
  { label: 'Intelligence', section: 'intelligence', links: [
    { path: '/admin/ceo', label: 'CEO Command', icon: 'vip-crown-line' },
    { path: '/admin/cb-system', label: 'CB System', icon: 'robot-2-line' },
    { path: '/admin/intelligence', label: 'Intelligence OS', icon: 'cpu-line' },
    // Moved out of the Program group 2026-09-08. The Enterprise Memory Graph
    // spans leads, agents, campaigns and projects; students are one node type
    // among many, so it sits with the other platform-wide intelligence
    // surfaces rather than beside the Accelerator.
    //
    // THE SECTION IS PINNED TO 'program' ON PURPOSE and must not be allowed to
    // inherit this group's 'intelligence'. The backend's mgmtSectionGate maps
    // /api/admin/brain to 'program'; changing the nav section without changing
    // the gate would make the two disagree, and changing BOTH would silently
    // re-scope who can open the page — an access decision, not a nav tidy-up.
    // This is the same presentation-vs-authorization split the Revenue group
    // already uses for Leads and Pipeline (grouped under Revenue, gated on
    // 'leads'). Moving a link between groups must never move access with it.
    { path: '/admin/brain', label: 'Enterprise Intelligence', icon: 'brain-line', section: 'program' },
    { path: '/admin/insights', label: 'Insights', icon: 'lightbulb-line' },
    { path: '/admin/governance', label: 'Governance', icon: 'shield-keyhole-line' },
    { path: '/admin/governance-policy', label: 'Governance Policies', icon: 'shield-star-line' },
  ]},
  { label: 'System', section: 'system', links: [
    { path: '/admin/tickets', label: 'Tickets', icon: 'ticket-2-line' },
    { path: '/admin/reports', label: 'Automated Reports', icon: 'mail-send-line' },
    { path: '/admin/settings', label: 'Settings', icon: 'settings-3-line' },
  ]},
];

/** Flat list for the "jump to" search — each link carries its section (a group's
 *  links inherit the group's section) so search results can be RBAC-filtered too. */
export const ALL_LINKS: NavLink[] = [
  ...PINNED_LINKS,
  ...NAV_GROUPS.flatMap((g) => g.links.map((l) => ({ ...l, section: l.section ?? g.section }))),
];

/**
 * Admin routes every authenticated admin identity may reach regardless of
 * section, because they are about the account itself rather than any data
 * surface. Kept tiny on purpose.
 */
export const UNIVERSAL_ADMIN_PATHS: readonly string[] = ['/admin/change-password'];

/**
 * Paths whose access the API decides, because one section key cannot express it.
 *
 * The nav's model is one section per path, and ProtectedRoute inherits it. The
 * People roster breaks that model: it legitimately serves five sections (leads,
 * revenue, students, program, career_review), so ANY single key here would bounce
 * roles the API authorises - pick 'students' and a revenue identity is refused a
 * page the backend would happily serve.
 *
 * Listing it here lets an authenticated admin reach the page, where the API makes
 * the real decision. That is not a loosening: /api/admin/people re-checks with
 * hasAnyPersonScope() - stricter than any single section, since it denies
 * community_organizer, who holds 'dashboard' - and applies the row-level
 * lifecycle scope inside the SQL. The page renders the 403 plainly when it comes.
 *
 * This mirrors the AGNOSTIC list in the backend's mgmtSectionGate deliberately,
 * so the two gates cannot drift into disagreeing about this one path.
 *
 * Keep this list tiny. A path belongs here only when it genuinely serves several
 * sections AND its API enforces scope itself.
 */
export const API_ENFORCED_PATHS: readonly string[] = ['/admin/people'];

/**
 * Admin routes that have a SECTION but deliberately no sidebar entry.
 *
 * Discovery for the Admin OS consolidation found 74 admin routes against 44 nav
 * paths. Twelve live surfaces had no nav entry at all, and the two gates then
 * disagreed about them: `sectionForPath()` returned null, so ProtectedRoute's
 * `allowed = section ? canSection(section) : !isScopedRep` ADMITTED every
 * mgmt-role identity, while the backend's `mgmtSectionGate` is deny-by-default
 * and 403d them. Not a data leak — the API is the gate that holds — but a
 * mentor could reach /admin/apollo and get a shell that failed every call with
 * nothing explaining why. That is the latent 403 the mgmtSectionGate comments
 * already warn about twice.
 *
 * Classifying them here fixes the disagreement without adding nine items to a
 * sidebar that is about to be reorganised into six domains. Each row must have
 * a matching prefix in the backend's PATH_SECTION, or the two gates drift
 * apart again — which is the whole failure this closes.
 *
 * Three further orphans were retired outright rather than classified
 * (/admin/refactored/builder, /admin/refactored/client, /admin/va-erp): each
 * documented itself as a prototype or demo-scope surface.
 */
export const UNLISTED_PATH_SECTIONS: ReadonlyArray<readonly [string, string]> = [
  // Acquisition tooling — sits with the lead-ingestion surfaces it feeds.
  ['/admin/apollo', 'lead_ingestion'],
  ['/admin/import', 'lead_ingestion'],
  // Tracking estate across the properties — the same section as Visitors.
  ['/admin/tracking-estate', 'campaigns'],
  // Executive summary — the Command Center's own job, so the landing section.
  ['/admin/executive-narrative', 'dashboard'],
  // Brand administration. The page lives at /admin/marketing/brands; this entry keeps the OLD
  // path classified, because /admin/brands still resolves (it redirects, preserving the query
  // string the LinkedIn OAuth callback returns with). Without it a scoped identity following a
  // bookmark is bounced off a path the API would have served: ProtectedRoute computes
  // `allowed = section ? canSection(section) : !isScopedRep`, so a null here and a real section
  // there disagree. Caught by adminNavMarketing.test.ts asserting the two sides agree.
  //
  // 'brands' as of 2026-10-16, matching the backend's PATH_SECTION remap. Only the LEGACY path
  // is listed here: /admin/marketing/brands is now a real nav link in the Brands group, and
  // sectionForPath searches both lists on the same longest-prefix rule, so listing it twice
  // would be duplication rather than belt and braces. Being a child of /admin/marketing does
  // not pull it back to 'campaigns' — the longer path wins.
  ['/admin/brands', 'brands'],
  // Audit ledger. Classified from what it QUERIES (event_type, actor,
  // entity_type, entity_id, payload) rather than from its name — it is a
  // system audit trail, not a marketing events page.
  ['/admin/events', 'system'],
  ['/admin/work-ledger-health', 'system'],
  ['/admin/automation', 'system'],
  // AI workforce and knowledge operations.
  ['/admin/agent-orphans', 'intelligence'],
  ['/admin/knowledge-ops', 'intelligence'],
  // Folded into the Accelerator page's tabs on 2026-09-08 and removed from the
  // Program nav group. Every one of these keeps a live route and a working
  // page, so each MUST keep its section: the backend's mgmtSectionGate maps
  // /api/admin/community, /api/admin/cert-prep, /api/admin/case-studies,
  // /api/admin/projects and /api/admin/feed-control all to 'program', and this
  // list is the frontend half of that contract. Without these rows
  // sectionForPath() returns null for the routes and ProtectedRoute bounces
  // every scoped identity off pages the API would happily serve — while a
  // legacy admin typing the URL still gets a working page, which is the
  // silent, role-dependent breakage this file warns about twice above.
  ['/admin/community-roles', 'program'],
  ['/admin/cert-prep', 'program'],
  ['/admin/case-studies', 'program'],
  ['/admin/projects', 'program'],
  ['/admin/feed-control-governance', 'program'],
  // Architecture Skills (the CAPE rubric) became a tab on the Curriculum page
  // in the same pass — curriculum configuration, authored by the people who
  // author the curriculum. Its route stays live and its section is unchanged.
  ['/admin/cape-settings', 'program'],
];

/**
 * The RBAC section governing an admin route, or null when the path has no nav
 * entry (detail routes under a nav path resolve to their parent's section).
 *
 * Longest-prefix wins so a more specific entry beats a shorter one, and the
 * match is `/`-delimited so '/admin/leads' never claims '/admin/leadsomething'.
 * `newTab` entries are external or bridge destinations, not routes in this app,
 * so they are skipped.
 */
export function sectionForPath(pathname: string): string | null {
  let bestPath = '';
  let bestSection: string | null = null;

  for (const link of ALL_LINKS) {
    if (link.newTab || !link.section) continue;
    if (pathname === link.path || pathname.startsWith(link.path + '/')) {
      if (link.path.length > bestPath.length) {
        bestPath = link.path;
        bestSection = link.section;
      }
    }
  }

  // Routes with a section but no sidebar entry. Same longest-prefix rule, so a
  // nav entry and an unlisted entry compete on specificity rather than on which
  // list they happen to live in.
  for (const [path, section] of UNLISTED_PATH_SECTIONS) {
    if (pathname === path || pathname.startsWith(path + '/')) {
      if (path.length > bestPath.length) {
        bestPath = path;
        bestSection = section;
      }
    }
  }

  return bestSection;
}

// Where a role would rather land, in order, before falling back to whatever it
// can reach. Keeps a sales rep on Leads instead of Pipeline purely because
// Pipeline sorts earlier in some future nav edit.
const LANDING_PREFERENCE = ['/admin/dashboard', '/admin/leads', '/admin/students'];

/**
 * The best landing route for an identity, given its section predicate. Used for
 * the post-login destination and as the bounce target when someone reaches a
 * route outside their scope, so the redirect always terminates somewhere real.
 */
export function firstAccessiblePath(canSection: (section: string) => boolean): string {
  for (const path of LANDING_PREFERENCE) {
    const section = sectionForPath(path);
    if (section && canSection(section)) return path;
  }
  const link = ALL_LINKS.find((l) => !l.newTab && canSection(l.section as string));
  return link ? link.path : UNIVERSAL_ADMIN_PATHS[0];
}

/**
 * The groups this viewer should see, with each group's links already filtered to their sections.
 *
 * ## The bug this fixes, reported from the outside
 *
 * Quincy Ninying, 2 Nov 2026: *"The Agents tab is still not in my sidebar. I signed out and back
 * in, and tried again in a private window. My menu shows only Dashboard, My Domain, Change
 * Requests, Tickets, Automated Reports and Settings. There is no refusal message, the tab is
 * simply not listed."*
 *
 * He was right, and his grants were never the problem: he holds `agents`
 * (`mgmt_role: platform` plus the extra), and the agent-factory feed returns his `platform` domain.
 *
 * The group survived or died on its LINKS alone. "Agents" is a sub-list of the Interns group, whose
 * one link needs `internship`. A viewer holding `agents` but not `internship` lost the whole group
 * before its sub-lists were ever looked at — so a node he was entitled to vanished with no refusal
 * to explain it, which is the worst version of this failure: nothing to search for and nothing to
 * report but an absence.
 *
 * Six of the seven agent builders are in that position (platform, marketing, curriculum,
 * product_experience, lead_intake, revenue). The seventh, Tashima Diah, holds `internship_review`
 * and therefore `internship` — and she is the one builder who did not report a missing tab, which
 * is the confirmation rather than the coincidence.
 *
 * ## What stays true
 *
 * A sub-list is still gated by its OWN section and still hidden when its feed is empty, so this
 * widens nothing: `hasSubListEntries` is the same row-scoped, server-side feed the node renders
 * from. Admissions, who holds `internship` and not `agents`, still never sees Agents — the case
 * `adminNav.ts`'s Interns comment is written to protect. A group kept by this rule alone renders
 * its header and its sub-list with no links under it, which is the honest picture: the viewer may
 * reach those agents and may not reach the Interns hub.
 */
export function visibleNavGroups(
  canSection: (section: string) => boolean,
  hasSubListEntries: (sl: NavSubList) => boolean,
): NavGroup[] {
  return NAV_GROUPS
    .map((g) => ({ ...g, links: g.links.filter((l) => canSection(l.section ?? g.section)) }))
    .filter((g) => g.links.length > 0
      || (g.subLists ?? []).some((sl) => canSection(sl.section) && hasSubListEntries(sl)));
}
